import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  PresenceUser,
  WsClientEnvelope,
  WsServerEnvelope,
} from '@edytech/shared/types';
import {
  WS_MAX_MESSAGES_PER_SEC,
  WS_HEARTBEAT_INTERVAL_MS,
  WS_RECONNECT_BACKOFF_MIN_MS,
  WS_RECONNECT_BACKOFF_MAX_MS,
} from '@edytech/shared/constants';
import { getAccessToken } from '../lib/api-client';
import { liveClassService } from '../services/live-class.service';

/**
 * Phase 3 — `useLiveRoom()` WebSocket client (TRD §4.3/§4.4/§6.6).
 *
 * - Connects to `wss(s)://host/api/ws/live-classes/:sessionId?token=<JWT>`
 *   (the axios token is reused; no other credential ever leaves the client).
 * - Auto-reconnects with exponential backoff 1s → 2s → 4s → … → 30s.
 * - `send()` throttles locally to `WS_MAX_MESSAGES_PER_SEC` (queue + drain).
 * - Heartbeats every `WS_HEARTBEAT_INTERVAL_MS`.
 * - `degraded` mode (server capped / WS rejected): switches to REST polling —
 *   `GET messages?after=` every 5s for receive, `apiPost` for send — so the
 *   room keeps working with no feature loss.
 */

export type LiveRoomStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'degraded' | 'closed';

export interface UseLiveRoomArgs {
  sessionId: number;
  enabled?: boolean;
  /** Every decoded server envelope (messages, votes, polls, moderation…). */
  onEnvelope?: (envelope: WsServerEnvelope) => void;
  /** Presence updates (feed the participant sidebar). */
  onPresence?: (users: PresenceUser[]) => void;
  /** Fired right after (re)connect so the caller can resync history with `?after=`. */
  onResync?: () => void;
}

export interface UseLiveRoomResult {
  status: LiveRoomStatus;
  degraded: boolean;
  /** Send a client envelope. Throttled in WS mode; mapped to REST in degraded mode. */
  send: (envelope: WsClientEnvelope) => void;
  lastError: string | null;
  /** True while the local send queue is throttled. */
  throttled: boolean;
}

const WS_SEND_TICK_MS = 200;
const DEGRADED_POLL_MS = 5_000;

function wsUrl(sessionId: number, token: string): string {
  const base = (import.meta.env.VITE_API_URL as string | undefined) || '/api';
  const apiUrl = new URL(base, window.location.origin);
  const protocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
  const path = `${apiUrl.pathname.replace(/\/$/, '')}/ws/live-classes/${sessionId}`;
  return `${protocol}//${apiUrl.host}${path}?token=${encodeURIComponent(token)}`;
}

export function useLiveRoom({
  sessionId,
  enabled = true,
  onEnvelope,
  onPresence,
  onResync,
}: UseLiveRoomArgs): UseLiveRoomResult {
  const [status, setStatus] = useState<LiveRoomStatus>('idle');
  const [degraded, setDegraded] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [throttled, setThrottled] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const queueRef = useRef<WsClientEnvelope[]>([]);
  const backoffRef = useRef(WS_RECONNECT_BACKOFF_MIN_MS);
  const everOpenedRef = useRef(false);
  const manualCloseRef = useRef(false);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const drainRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const degradedPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const degradedRef = useRef(false);
  const enabledRef = useRef(enabled);
  const onEnvelopeRef = useRef(onEnvelope);
  const onPresenceRef = useRef(onPresence);
  const onResyncRef = useRef(onResync);
  // Recursion seam between connect/schedule/initial-failure (refs, not render writes).
  const connectRef = useRef<() => void>(() => undefined);
  const scheduleRef = useRef<() => void>(() => undefined);
  const failRef = useRef<() => void>(() => undefined);

  // Keep the latest prop values in refs via an effect (React hooks lint: no
  // ref writes during render).
  useEffect(() => {
    enabledRef.current = enabled;
    onEnvelopeRef.current = onEnvelope;
    onPresenceRef.current = onPresence;
    onResyncRef.current = onResync;
  });

  /** When the WS is rejected/capped, fall back to REST polling (TRD §4.4). */
  const enterDegraded = useCallback(() => {
    degradedRef.current = true;
    setDegraded(true);
    setStatus('degraded');
    manualCloseRef.current = true;
    // REST polling receiver — history resync every 5s keeps the panel live.
    degradedPollRef.current = setInterval(() => {
      if (!enabledRef.current) return;
      void liveClassService
        .getMessages(sessionId, { limit: 200 })
        .then((page) => {
          for (const m of page.messages) {
            if (m.messageType === 'CHAT') {
              onEnvelopeRef.current?.({ type: 'chat.message', message: m });
            } else if (m.messageType === 'QUESTION') {
              onEnvelopeRef.current?.({ type: 'qa.question', message: m });
            } else if (m.messageType === 'ANNOUNCEMENT') {
              onEnvelopeRef.current?.({ type: 'announcement', message: m });
            } else if (m.messageType === 'POLL') {
              onEnvelopeRef.current?.({ type: 'poll.created', poll: m });
            }
          }
        })
        .catch(() => undefined);
    }, DEGRADED_POLL_MS);
  }, [sessionId]);

  /** Drain the local 5 msg/s send queue while the socket is open. */
  const startDrainer = useCallback(() => {
    if (drainRef.current) return;
    const perTick = Math.max(1, Math.floor((WS_MAX_MESSAGES_PER_SEC * WS_SEND_TICK_MS) / 1000));
    drainRef.current = setInterval(() => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      let sent = 0;
      while (sent < perTick && queueRef.current.length > 0) {
        const next = queueRef.current.shift();
        if (next) ws.send(JSON.stringify(next));
        sent++;
      }
      setThrottled(queueRef.current.length > 0);
    }, WS_SEND_TICK_MS);
  }, []);

  const connect = useCallback(() => {
    if (!enabledRef.current || degradedRef.current || manualCloseRef.current) return;
    const token = getAccessToken();
    if (!token) {
      setLastError('You must be signed in to join the live room.');
      setStatus('closed');
      return;
    }
    setStatus('connecting');
    setLastError(null);
    let ws: WebSocket;
    try {
      ws = new WebSocket(wsUrl(sessionId, token));
    } catch {
      setLastError('Could not open a WebSocket connection.');
      setStatus('closed');
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      if (!enabledRef.current) return;
      everOpenedRef.current = true;
      backoffRef.current = WS_RECONNECT_BACKOFF_MIN_MS;
      setStatus('open');
      startDrainer();
      // Join + resync history (no duplicates — keyset `?after=`).
      ws.send(JSON.stringify({ type: 'presence.join' } satisfies WsClientEnvelope));
      onResyncRef.current?.();
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      heartbeatRef.current = setInterval(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'heartbeat', t: Date.now() } satisfies WsClientEnvelope));
        }
      }, WS_HEARTBEAT_INTERVAL_MS);
    };

    ws.onmessage = (event) => {
      if (typeof event.data !== 'string') return;
      let envelope: WsServerEnvelope;
      try {
        envelope = JSON.parse(event.data) as WsServerEnvelope;
      } catch {
        return;
      }
      if (envelope.type === 'degraded') {
        enterDegraded();
        return;
      }
      if (envelope.type === 'error') {
        setLastError(envelope.message);
        return;
      }
      if (envelope.type === 'presence.update') {
        onPresenceRef.current?.(envelope.users);
      }
      onEnvelopeRef.current?.(envelope);
    };

    ws.onclose = (event) => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      if (manualCloseRef.current || degradedRef.current) return;
      if (event.code === 1013) {
        // Server-rejected (room full / resync) → REST fallback.
        enterDegraded();
        return;
      }
      if (!everOpenedRef.current) {
        failRef.current();
        return;
      }
      scheduleRef.current();
    };

    ws.onerror = () => {
      // onclose fires next; error alone is not actionable in the browser API.
    };
  }, [sessionId, enterDegraded, startDrainer]);

  /** Reconnect with exponential backoff 1s → 2s → 4s → … → max 30s (TRD §6.6). */
  const scheduleReconnect = useCallback(() => {
    if (manualCloseRef.current || degradedRef.current) return;
    const delay = backoffRef.current;
    backoffRef.current = Math.min(backoffRef.current * 2, WS_RECONNECT_BACKOFF_MAX_MS);
    setStatus('reconnecting');
    const timer = setTimeout(() => {
      if (!manualCloseRef.current && !degradedRef.current) connectRef.current();
    }, delay);
    return () => clearTimeout(timer);
  }, []);

  /** Initial-connect failure: fall back to REST only when REST is actually usable. */
  const handleInitialFailure = useCallback(() => {
    void liveClassService
      .getMessages(sessionId, { limit: 1 })
      .then(() => enterDegraded())
      .catch((e) => {
        const statusCode =
          e && typeof e === 'object' && 'response' in e
            ? ((e as { response?: { status?: number } }).response?.status ?? 0)
            : 0;
        if (statusCode === 401 || statusCode === 403) {
          manualCloseRef.current = true;
          setStatus('closed');
          setLastError('You are not authorized to join this room.');
        } else {
          scheduleRef.current();
        }
      });
  }, [sessionId, enterDegraded]);

  // Wire the recursion seam after every render (refs, not render writes).
  useEffect(() => {
    connectRef.current = connect;
    scheduleRef.current = scheduleReconnect;
    failRef.current = handleInitialFailure;
  });

  useEffect(() => {
    if (!enabled || degradedRef.current) return;
    connect();
    return () => {
      manualCloseRef.current = true;
      if (wsRef.current) wsRef.current.close(1000, 'leave');
      wsRef.current = null;
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      if (drainRef.current) {
        clearInterval(drainRef.current);
        drainRef.current = null;
      }
      if (degradedPollRef.current) {
        clearInterval(degradedPollRef.current);
        degradedPollRef.current = null;
      }
    };
  }, [enabled, sessionId, connect]);

  const send = useCallback(
    (envelope: WsClientEnvelope) => {
      if (!enabledRef.current) return;
      if (degradedRef.current) {
        // REST fallback parity (TRD §4.4): send via the REST endpoints.
        void sendViaRest(sessionId, envelope);
        return;
      }
      queueRef.current.push(envelope);
    },
    [sessionId]
  );

  return { status, degraded, send, lastError, throttled };
}

/** Degraded-mode REST mapping for WS client envelopes (no feature loss). */
async function sendViaRest(sessionId: number, envelope: WsClientEnvelope): Promise<void> {
  switch (envelope.type) {
    case 'chat.send':
      await liveClassService.sendMessage(sessionId, { messageType: 'CHAT', content: envelope.content });
      break;
    case 'qa.ask':
      await liveClassService.sendMessage(sessionId, { messageType: 'QUESTION', content: envelope.content });
      break;
    case 'qa.vote':
      await liveClassService.voteMessage(envelope.messageId);
      break;
    case 'poll.vote':
      await liveClassService.votePoll(sessionId, envelope.pollId, envelope.optionId);
      break;
    case 'moderation.hide':
      await liveClassService.moderateMessage(envelope.messageId, 'hide');
      break;
    case 'moderation.pin':
      await liveClassService.moderateMessage(envelope.messageId, 'pin', { pinned: envelope.pinned });
      break;
    case 'moderation.answer':
      await liveClassService.moderateMessage(envelope.messageId, 'answer');
      break;
    case 'raise.hand':
    case 'heartbeat':
    case 'presence.join':
    case 'presence.leave':
      // No REST equivalent (presence is WS-only); heartbeats are irrelevant when polling.
      break;
    default:
      break;
  }
}


